import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const AGENT_BASE = 'http://127.0.0.1:9900/v1';
const DETECT_TIMEOUT_MS = 1500;

export interface AgentStatus {
  version: string;
  modules: { [name: string]: { healthy: boolean; message?: string } };
}

export interface PrintResult {
  ok: boolean;
  message: string;
}

export interface PreferredPrinter {
  id: string | null;
  display_name: string;
  /** "user-config" (escolhida na UI do agent), "system-default" (default do SO) ou "none" */
  source: 'user-config' | 'system-default' | 'none';
  available: boolean;
}

/**
 * Cliente do servidor HTTP local exposto pelo `ladder-agent` em :9900.
 * Detecta disponibilidade com cache leve para evitar bater no localhost
 * a cada operação.
 */
@Injectable({ providedIn: 'root' })
export class LadderAgentService {
  public available$ = new BehaviorSubject<boolean>(false);
  public version$ = new BehaviorSubject<string | null>(null);

  private lastDetect = 0;
  private detectCacheMs = 5000;

  private cachedPreferred: PreferredPrinter | null = null;
  private lastPreferredFetch = 0;

  /** Verifica se o agent está rodando localmente. Resultado fica em cache por 5s. */
  public async isAvailable(): Promise<boolean> {
    const now = Date.now();
    if (now - this.lastDetect < this.detectCacheMs) {
      return this.available$.value;
    }
    this.lastDetect = now;

    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), DETECT_TIMEOUT_MS);
      const res = await fetch(`${AGENT_BASE}/status`, { signal: ctrl.signal });
      clearTimeout(timer);
      if (!res.ok) {
        this.available$.next(false);
        return false;
      }
      const data: AgentStatus = await res.json();
      this.version$.next(data.version);
      this.available$.next(true);
      return true;
    } catch {
      this.available$.next(false);
      this.version$.next(null);
      return false;
    }
  }

  /**
   * Retorna a impressora preferida configurada pelo usuário no agent (UI Tauri),
   * ou a default do SO se não houver. Resultado em cache de 5s.
   */
  public async getPreferredPrinter(): Promise<PreferredPrinter | null> {
    const now = Date.now();
    if (this.cachedPreferred && now - this.lastPreferredFetch < this.detectCacheMs) {
      return this.cachedPreferred;
    }
    if (!(await this.isAvailable())) {
      this.cachedPreferred = null;
      return null;
    }

    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), DETECT_TIMEOUT_MS);
      const res = await fetch(`${AGENT_BASE}/print/preferred`, { signal: ctrl.signal });
      clearTimeout(timer);
      if (!res.ok) {
        return null;
      }
      this.cachedPreferred = await res.json();
      this.lastPreferredFetch = now;
      return this.cachedPreferred;
    } catch {
      return null;
    }
  }

  /** Envia bytes ESC/POS (em base64) para o agent imprimir. */
  public async printEscPos(dataBase64: string, printerId?: string): Promise<PrintResult> {
    const body: { data_base64: string; printer_id?: string } = { data_base64: dataBase64 };
    if (printerId) body.printer_id = printerId;

    const res = await fetch(`${AGENT_BASE}/print/escpos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    return res.json();
  }

  /**
   * Lê o status completo do agente — sem cache, propaga exceções de rede.
   * Diferente de `isAvailable()`, expõe a versão para uso pelo orquestrador
   * de update.
   */
  public async getStatus(): Promise<AgentStatus> {
    const res = await fetch(`${AGENT_BASE}/status`);
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data = (await res.json()) as AgentStatus;
    this.version$.next(data.version);
    this.available$.next(true);
    return data;
  }

  /**
   * Repassa instrução de auto-update ao agente local. O agente baixa o
   * binário, valida a assinatura minisign contra a chave pública embarcada,
   * dispara o instalador e encerra o processo — a UI deve assumir que o
   * serviço fica indisponível por alguns segundos após esta chamada.
   */
  public async applyUpdate(payload: {
    url: string;
    signature: string;
    version: string;
  }): Promise<{ ok: boolean; message: string; restarting?: boolean }> {
    const res = await fetch(`${AGENT_BASE}/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({ ok: false, message: 'resposta inválida do agent' }));
    if (!res.ok) {
      throw new Error(data?.message || `HTTP ${res.status}`);
    }
    // Invalida caches — o agente vai reiniciar.
    this.lastDetect = 0;
    this.cachedPreferred = null;
    this.available$.next(false);
    return data;
  }
}
